import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useBranding, setBranding, logoSrc } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { ImageUp, RotateCcw } from "lucide-react";

const SLOTS = [
  { id: "logo_light", label: "Main logo (light backgrounds)", where: "Website header, member portal, staff and admin headers", bg: "#ffffff" },
  { id: "logo_dark", label: "Footer logo (dark background)", where: "Website footer and email header", bg: "#003c53" },
];

function Slot({ slot, branding }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (call, msg) => {
    setBusy(true);
    try { const { data } = await call(); setBranding(data); toast.success(msg); }
    catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(() => api.post(`/admin/branding/${slot.id}`, fd), "Logo updated everywhere");
  };
  return (
    <div className="rounded-xl p-4 space-y-3" style={{ border: "1px solid var(--border)" }} data-testid={`branding-slot-${slot.id}`}>
      <div>
        <div className="font-semibold text-sm">{slot.label}</div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{slot.where}</div>
      </div>
      <div className="h-24 rounded-lg flex items-center justify-center p-3 overflow-hidden" style={{ background: slot.bg, border: "1px solid var(--border)" }}>
        <img src={logoSrc(branding[slot.id])} alt={slot.label} className="h-full w-auto max-w-full object-contain" data-testid={`branding-preview-${slot.id}`} />
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])}
             data-testid={`branding-file-${slot.id}`} />
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={busy} onClick={() => ref.current?.click()} className="rounded-full bg-navy text-white"
                data-testid={`branding-upload-${slot.id}`}><ImageUp className="h-4 w-4 mr-1.5" />{busy ? "Uploading…" : "Upload new"}</Button>
        {branding.custom?.[slot.id] && (
          <Button type="button" size="sm" variant="outline" disabled={busy} className="rounded-full"
                  onClick={() => run(() => api.delete(`/admin/branding/${slot.id}`), "Restored the default logo")}
                  data-testid={`branding-reset-${slot.id}`}><RotateCcw className="h-4 w-4 mr-1.5" />Use default</Button>
        )}
      </div>
    </div>
  );
}

export default function BrandingCard() {
  const branding = useBranding();
  return (
    <div className="w-card w-card-bordered p-6 space-y-4 mt-6" data-testid="branding-card">
      <div>
        <h3 className="font-bold">Logo</h3>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          Upload once and it updates the website, member portal and emails. PNG, JPG or WebP, up to 2 MB. A transparent PNG works best.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {SLOTS.map((s) => <Slot key={s.id} slot={s} branding={branding} />)}
      </div>
    </div>
  );
}
